// src/pages/Fotos.tsx — CON-TOOLS-04 v1.4 (Nov 1, 2026, PAGES-ART-01: custom quotes carry several labeled pictures): "Pictures on a quote" —
//   type a quote number, see its pictures (kind, position, label, frozen), attach a concept image you made elsewhere (label + position, up
//   to 4 per quote; frozen at once if the quote is already sent), or remove one. Needs gm-quote-render v2.8 + record 89.
// v1.3 (Nov 1, 2026, owner: no placement polygons → free edits, "Switch it too"): "Where it goes" (the
//   corner editor) is gone — gm-quote-render v2.7 edits the whole photo from one brief, so the team writes WHAT to change and WHERE in the
//   notes box. Option B (download his photo / upload your picture) and "Use anyway" stay.
// v1.2 (Oct 30, 2026, owner: "download a photo provided by a contractor, and … upload a rendered image …
//   so the Team is not locked into one idea"): two ways to make the picture, side by side — A) Green Maestro AI, B) your own tool:
//   ⬇ Download his photo (the saved copy, long side up to 1600 px) → edit anywhere → ⬆ Upload your picture → tick it → Deliver.
// v1.1 (Oct 30, 2026, owner: "I just want the render with AI to work"): the team places the work.
//   "Where it goes" shows his photo with the areas (drag the corners); anything he picked that has no area is listed with "＋ Place it", and
//   "Make with AI" sends the areas as drawn here (gm-quote-render v2.3 saves them on the request). A picture the checker rejected is still
//   shown with the reason, and "Use anyway" lets the team deliver it — a person on the team is the judge, not the checker.
// v1 (Oct 29, 2026). Admin › Fotos para Maestros: "✨ Que el equipo GM te la haga" requests.
// Promise to the Maestro: same day (owner R4). Every request texted the platform phone when it came in (R5).
// All reads and writes go through gm-quote-render v2.0 ops actions, which check ops_role() (pricing_admin / owner) with this sign-in.
// Pictures and voice notes are private: the URLs here are signed for 2 hours. Customer house photos never leave this page.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

interface Pic { id: string; status: string; url: string | null; check?: { ok?: boolean; reason?: string; note?: string; not_drawn?: { line_no: number; description: string }[] } | null; error?: string | null }
type Pt = [number, number];
interface Area { line_no: number; shape: "quad" | "line"; points: Pt[] }
interface Req {
  id: number; status: "new" | "working" | "delivered" | "cancelled"; maestro: string; lang: string; quote_number: string | null; quote_status: string | null;
  note: string | null; voice_url: string | null; source_url: string | null; items: { item_no: number; label_es: string; label_en: string; quantity: number | null; unit: string | null }[] | null;
  edit_area: Area[] | null; due_date: string; overdue: boolean; due_today: boolean; claimed_by: string | null; price_cents: number; ops_alert_sent: number;
  created_at: string; delivered_at: string | null; cancel_reason: string | null; delivered_render_ids: string[]; tries: Pic[]; team: Pic[];
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("gm-quote-render", { body });
  if (error) {
    let b: { error?: string; message_en?: string } | null = null;
    try { b = await (error as { context?: Response }).context?.json?.() ?? null; } catch { /* generic */ }
    throw new Error(b?.message_en ?? b?.error ?? "Something went wrong.");
  }
  return data as T;
}
// v1.2: save his photo to this computer (signed URL → file); if the browser blocks it, open it in a new tab instead
async function download(url: string, name: string) {
  try {
    const res = await fetch(url); if (!res.ok) throw new Error(String(res.status));
    const obj = URL.createObjectURL(await res.blob());
    const a = document.createElement("a"); a.href = obj; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(obj), 10_000);
  } catch { window.open(url, "_blank", "noopener"); }
}
async function fileToB64(file: File, maxSide = 1600): Promise<string> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const c = document.createElement("canvas"); c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
  const blob: Blob | null = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.9));
  if (!blob) throw new Error("Couldn't read that image.");
  const buf = new Uint8Array(await blob.arrayBuffer()); let s = "";
  for (let i = 0; i < buf.length; i += 0x8000) s += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(s);
}
const when = (iso: string) => new Date(iso).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const S = {
  card: { background: "#fff", border: "1px solid #ddd", borderRadius: 12, padding: 14, marginBottom: 14 } as const,
  btn: { minHeight: 40, padding: "0 14px", borderRadius: 8, border: "1px solid #1F3D2B", background: "#fff", color: "#1F3D2B", fontWeight: 600, cursor: "pointer" } as const,
  main: { minHeight: 40, padding: "0 14px", borderRadius: 8, border: "1px solid #1F3D2B", background: "#1F3D2B", color: "#fff", fontWeight: 600, cursor: "pointer" } as const,
  thumb: { width: 110, height: 146, objectFit: "cover" as const, borderRadius: 8, display: "block" },
  muted: { color: "#666", fontSize: 13 } as const,
};


// v1.4: pictures on a custom quote
interface QPic { id: string; status: string; kind: "edit" | "concept"; position: number | null; label: string | null; origin: string; frozen: boolean; url: string | null }
function QuotePictures() {
  const [num, setNum] = useState("");
  const [data, setData] = useState<{ quote: { quote_number: string; status: string; source: string }; pictures: QPic[] } | null>(null);
  const [label, setLabel] = useState("");
  const [pos, setPos] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = async (n = num) => {
    setErr(null); setBusy("load");
    try { setData(await call({ action: "quote_pictures", quote_number: n.trim() })); } catch (e) { setData(null); setErr((e as Error).message); }
    setBusy(null);
  };
  const chosen = (data?.pictures ?? []).filter((p) => p.status === "chosen");
  return (
    <div style={{ ...S.card, marginBottom: 18 }}>
      <h3 style={{ margin: "0 0 6px" }}>Pictures on a quote</h3>
      <p style={S.muted}>Up to 4 pictures per custom quote, in order. Concepts show on the customer page as a plain image with "Concept rendering — actual finish and layout set on site".</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="GMQ-2026-00123" style={{ minHeight: 40, padding: "0 10px", borderRadius: 8, border: "1px solid #ccc" }} />
        <button type="button" style={S.btn} disabled={!num.trim() || !!busy} onClick={() => void load()}>{busy === "load" ? "Loading…" : "Open"}</button>
      </div>
      {err && <p style={{ color: "#C0392B" }} role="alert">{err}</p>}
      {data && (
        <div style={{ marginTop: 10 }}>
          <div style={S.muted}>{data.quote.quote_number} · {data.quote.status} · {data.quote.source} · {chosen.length}/4 on the quote</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
            {chosen.map((p) => (
              <figure key={p.id} style={{ margin: 0, maxWidth: 140 }}>
                {p.url ? <img src={p.url} alt="" style={S.thumb} /> : <div style={{ ...S.thumb, background: "#eee" }} />}
                <figcaption style={{ fontSize: 12 }}>
                  <b>#{p.position}</b> {p.kind}{p.frozen ? " · 🔒 sent" : ""}<br />{p.label ?? <span style={S.muted}>no label</span>}<br />
                  <button type="button" style={{ ...S.btn, minHeight: 30, padding: "0 8px", marginTop: 4, borderColor: "#C0392B", color: "#C0392B" }} disabled={!!busy}
                    onClick={async () => { if (!window.confirm(`Remove picture #${p.position} from ${data.quote.quote_number}?`)) return; setBusy("rm");
                      try { await call({ action: "quote_remove_picture", render_id: p.id }); await load(data.quote.quote_number); } catch (e) { setErr((e as Error).message); } setBusy(null); }}>Remove</button>
                </figcaption>
              </figure>
            ))}
          </div>
          {chosen.length < 4 && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
              <input value={label} onChange={(e) => setLabel(e.target.value.slice(0, 60))} placeholder="Label, e.g. Room 1 — 8×16" style={{ minHeight: 40, padding: "0 10px", borderRadius: 8, border: "1px solid #ccc", minWidth: 220 }} />
              <select value={pos} onChange={(e) => setPos(Number(e.target.value))} style={{ minHeight: 40 }} aria-label="position">
                <option value={0}>next free spot</option>{[1, 2, 3, 4].map((n) => <option key={n} value={n} disabled={chosen.some((p) => p.position === n)}>position {n}</option>)}
              </select>
              <label style={{ ...S.main, display: "inline-flex", alignItems: "center" }}>
                {busy === "attach" ? "Attaching…" : "⬆ Attach concept image"}
                <input type="file" accept="image/*" hidden onChange={async (e) => {
                  const f = e.target.files?.[0]; e.target.value = ""; if (!f) return; setBusy("attach"); setErr(null);
                  try { await call({ action: "quote_attach_concept", quote_number: data.quote.quote_number, photo_b64: await fileToB64(f, 2048), mime: "image/jpeg", label, position: pos || null });
                    setLabel(""); setPos(0); await load(data.quote.quote_number); } catch (x) { setErr((x as Error).message); }
                  setBusy(null);
                }} />
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Fotos() {
  const [params] = useSearchParams();
  const focus = Number(params.get("r")) || null;
  const [reqs, setReqs] = useState<Req[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [sel, setSel] = useState<Record<number, string[]>>({});
  const [teamNote, setTeamNote] = useState<Record<number, string>>({});
  const [tries, setTries] = useState<Record<number, number>>({});
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    try { const r = await call<{ requests: Req[] }>({ action: "help_queue" }); setReqs(r.requests); setErr(null); }
    catch (e) { setErr(String((e as Error).message)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (focus && reqs) document.getElementById(`req-${focus}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [focus, reqs]);

  async function act(id: number, label: string, body: Record<string, unknown>) {
    setBusy(`${id}:${label}`); setErr(null);
    try { await call({ ...body, id }); await load(); }
    catch (e) { setErr(`#${id}: ${(e as Error).message}`); }
    setBusy(null);
  }
  const toggle = (id: number, pic: string) => setSel((cur) => {
    const now = cur[id] ?? [];
    return { ...cur, [id]: now.includes(pic) ? now.filter((x) => x !== pic) : now.length >= 3 ? now : [...now, pic] };
  });

  const open = (reqs ?? []).filter((r) => r.status === "new" || r.status === "working");
  const done = (reqs ?? []).filter((r) => r.status === "delivered" || r.status === "cancelled");
  const dueToday = open.filter((r) => r.due_today).length;

  const render = (r: Req) => {
    const isOpen = r.status === "new" || r.status === "working";
    const picked = sel[r.id] ?? [];
    const b = (label: string) => busy === `${r.id}:${label}`;
    return (
      <div key={r.id} id={`req-${r.id}`} style={{ ...S.card, ...(r.id === focus ? { border: "2px solid #A3FF12" } : {}), ...(r.overdue ? { borderLeft: "6px solid #C0392B" } : {}) }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline", justifyContent: "space-between" }}>
          <b style={{ fontSize: 16 }}>#{r.id} · {r.maestro} · {r.quote_number ?? "no quote (tool)"}</b>
          <span style={{ fontSize: 13, fontWeight: 700, color: r.overdue ? "#C0392B" : r.due_today ? "#B7791F" : "#1F3D2B" }}>
            {r.status === "new" ? "NEW" : r.status === "working" ? "WORKING" : r.status.toUpperCase()}
            {isOpen ? (r.overdue ? " · OVERDUE (promised same day)" : " · due today") : ""}</span>
        </div>
        <div style={S.muted}>
          Asked {when(r.created_at)} · {r.lang} · price {r.price_cents ? `$${(r.price_cents / 100).toFixed(2)}` : "free"} · platform text {r.ops_alert_sent ? "sent" : "NOT sent"}
          {r.claimed_by ? ` · ${r.status === "working" ? "taken by" : "by"} ${r.claimed_by}` : ""}{r.cancel_reason ? ` · cancelled: ${r.cancel_reason}` : ""}
        </div>
        {r.note && <p style={{ margin: "8px 0", fontSize: 15, background: "#F6F6F2", padding: 10, borderRadius: 8 }}>“{r.note}”</p>}
        {r.voice_url && <audio src={r.voice_url} controls style={{ width: "100%", margin: "4px 0" }} />}
        {r.items && <div style={{ fontSize: 14, margin: "4px 0" }}><b>Show:</b> {r.items.map((i) => `${i.label_en}${i.quantity ? ` (${i.quantity}${i.unit ? " " + i.unit : ""})` : ""}`).join(" · ")}</div>}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
          {r.source_url && <figure style={{ margin: 0 }}><img src={r.source_url} alt="Maestro's photo" style={S.thumb} /><figcaption style={S.muted}>his photo</figcaption></figure>}
          {r.tries.map((t) => t.url && <figure key={t.id} style={{ margin: 0 }}><img src={t.url} alt="" style={{ ...S.thumb, opacity: t.status === "failed" ? 0.5 : 1 }} />
            <figcaption style={S.muted}>his try{t.status === "failed" ? " (failed)" : ""}</figcaption></figure>)}
        </div>
        {(r.team.length > 0 || isOpen) && <h4 style={{ margin: "12px 0 6px" }}>Team pictures {isOpen ? "— tick 1 to 3 to deliver" : ""}</h4>}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {r.team.map((t) => (
            <label key={t.id} style={{ cursor: isOpen && t.url ? "pointer" : "default" }}>
              {t.url ? <img src={t.url} alt="" style={{ ...S.thumb, outline: picked.includes(t.id) || r.delivered_render_ids.includes(t.id) ? "3px solid #A3FF12" : "none" }} />
                     : <div style={{ ...S.thumb, background: "#eee", display: "grid", placeItems: "center", fontSize: 12 }}>{t.status}</div>}
              <div style={{ fontSize: 12, maxWidth: 110 }}>
                {isOpen && t.url && <input type="checkbox" checked={picked.includes(t.id)} onChange={() => toggle(r.id, t.id)} />}{" "}
                {t.check?.ok ? "checker ok"
                  : t.url && isOpen ? <><b>Use anyway</b><br /><span style={S.muted}>checker: {t.check?.reason ?? t.status}{t.check?.note ? ` — ${t.check.note}` : ""}</span></>
                  : t.check?.reason ? `checker: ${t.check.reason}` : t.error ? t.error.slice(0, 40) : t.status}
                {t.check?.not_drawn?.length ? <div style={S.muted}>not drawn: {t.check.not_drawn.map((n) => n.description).join(", ")}</div> : null}
              </div>
            </label>
          ))}
        </div>
        {isOpen && (
          <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
            {r.status === "new" && <button type="button" style={S.btn} disabled={!!busy} onClick={() => act(r.id, "claim", { action: "help_claim" })}>{b("claim") ? "Taking…" : "Take it"}</button>}
            <textarea rows={2} placeholder="What to change and where — e.g. pergola on the left by the fence, 14×10, dark stain; remove the wheelbarrow" value={teamNote[r.id] ?? r.note ?? ""}
              onChange={(e) => setTeamNote((c) => ({ ...c, [r.id]: e.target.value }))} style={{ width: "100%", boxSizing: "border-box", padding: 8, borderRadius: 8, border: "1px solid #ccc" }} />
            <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>A · Green Maestro AI</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select value={tries[r.id] ?? 2} onChange={(e) => setTries((c) => ({ ...c, [r.id]: Number(e.target.value) }))} style={{ minHeight: 40 }} aria-label="tries">
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} {n === 1 ? "try" : "tries"}</option>)}
              </select>
              <button type="button" style={S.btn} disabled={!!busy}
                onClick={() => act(r.id, "ai", { action: "help_render", team_note: teamNote[r.id] ?? r.note ?? "", tries: tries[r.id] ?? 2 })}>
                {b("ai") ? "Making pictures… (30–60 s)" : "Make with AI"}</button>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>B · Your own tool</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button type="button" style={S.btn} disabled={!r.source_url}
                onClick={() => r.source_url && void download(r.source_url, `GM-request-${r.id}-${r.maestro.replace(/[^A-Za-z0-9]+/g, "")}-photo.jpg`)}>
                ⬇ Download his photo</button>
              <label style={{ ...S.btn, display: "inline-flex", alignItems: "center" }}>
                {b("upload") ? "Uploading…" : "⬆ Upload your picture"}
                <input type="file" accept="image/*" hidden onChange={async (e) => {
                  const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
                  try { const b64 = await fileToB64(f); await act(r.id, "upload", { action: "help_upload", photo_b64: b64, mime: "image/jpeg" }); }
                  catch (x) { setErr(`#${r.id}: ${(x as Error).message}`); }
                }} />
              </label>
            </div>
            <div style={S.muted}>Download → edit in any tool → upload the result. It shows under Team pictures; tick it and Deliver. (Photos are up to 1600 px on the long side; keep the same framing.)</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" style={S.main} disabled={!!busy || picked.length === 0}
                onClick={() => act(r.id, "deliver", { action: "help_deliver", render_ids: picked })}>
                {b("deliver") ? "Delivering…" : `Deliver ${picked.length || ""} and text ${r.maestro}`}</button>
              <button type="button" style={{ ...S.btn, borderColor: "#C0392B", color: "#C0392B" }} disabled={!!busy}
                onClick={() => { const reason = window.prompt(`Why can't we make it? ${r.maestro} gets this in a text (${r.lang}).`); if (reason) void act(r.id, "cancel", { action: "help_cancel", reason }); }}>
                Cancel…</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 16 }}>
      <QuotePictures />   {/* v1.4 */}
      <h2 style={{ marginBottom: 4 }}>Fotos para Maestros {dueToday > 0 && <span style={{ background: "#C0392B", color: "#fff", borderRadius: 12, padding: "2px 10px", fontSize: 14 }}>{dueToday} due today</span>}</h2>
      <p style={S.muted}>“✨ Que el equipo GM te la haga” — we promise <b>same day</b>. The Maestro gets a text when you deliver; delivered pictures land in his library and, if the quote is still open, on the quote.</p>
      {err && <p style={{ color: "#C0392B" }} role="alert">{err}</p>}
      {!reqs ? <p className="loading">Loading…</p> : open.length === 0 ? <p>No open requests. 🎉</p> : open.map(render)}
      <button type="button" style={{ ...S.btn, marginTop: 8 }} onClick={() => setShowDone((v) => !v)}>{showDone ? "Hide" : "Show"} delivered / cancelled ({done.length})</button>
      {showDone && <div style={{ marginTop: 12 }}>{done.map(render)}</div>}
    </div>
  );
}
